import APTable, {
  APHeaderRow,
  APTableProps,
} from "../../../../components/APTable";
import { toDollars } from "../../../../helpers/currencies";
import { FinanceComparisonCalculations } from "../../../../types/Calculation";

type TableDataDefinition = {
  method: string;
  upfrontCost: string;
  rate: string;
  totalInterest: string;
  lifetimeCost: string;
  weeklyLifetimeCost: string;
  costComparedToNovated: string;
};

const getTableRow = (
  method: "Cash" | "Home Loan" | "Car Loan" | "Novated",
  upfrontCost: number | null,
  rate: number,
  totalInterest: number | null,
  lifetimeCost: number,
  weeklyLifetimeCost: number,
  novatedLifetimeCost: number
): Record<keyof TableDataDefinition, string> => {
  const percentageComparedToNovated =
    (weeklyLifetimeCost / novatedLifetimeCost - 1) * 100;
  const isNovated = method === "Novated";

  return {
    method: method,
    upfrontCost: upfrontCost ? toDollars(upfrontCost, 2) : "-",
    rate: `${rate}%`,
    totalInterest: totalInterest ? toDollars(totalInterest, 2) : "-",
    lifetimeCost: toDollars(lifetimeCost, 2),
    weeklyLifetimeCost: toDollars(weeklyLifetimeCost, 2),
    costComparedToNovated: isNovated
      ? "-"
      : `${percentageComparedToNovated.toFixed(0)}%`,
  };
};

const getTableHeaderRows = (): Record<
  keyof TableDataDefinition,
  APHeaderRow
> => {
  return {
    method: { title: "FinanceMethod" },
    upfrontCost: { title: "Upfront Cost" },
    rate: { title: "Net Interest Rate" },
    totalInterest: { title: "Total Interest" },
    lifetimeCost: { title: "Lifetime Cost" },
    weeklyLifetimeCost: { title: "Weekly Lifetime Cost" },
    costComparedToNovated: { title: "Cost Compared to Novated" },
  };
};

const FinanceComparisonTable = ({
  calculations,
}: {
  calculations: FinanceComparisonCalculations;
}) => {
  const rows = [
    getTableRow(
      "Novated",
      null,
      calculations.inputs.novatedInterestRate,
      null,
      calculations.inputs.netCost.total,
      calculations.inputs.netCost.weekly,
      calculations.inputs.netCost.weekly
    ),
    getTableRow(
      "Cash",
      calculations.inputs.onroadVehiclePrice,
      calculations.inputs.cashInterestRate,
      calculations.cash.interestForgone.result,
      calculations.cash.lifetimeCost.result.total,
      calculations.cash.lifetimeCost.result.weekly,
      calculations.inputs.netCost.weekly
    ),
    getTableRow(
      "Home Loan",
      null,
      calculations.inputs.homeLoanInterestRate,
      calculations.homeloan.interest.result,
      calculations.homeloan.lifetimeCost.result.total,
      calculations.homeloan.lifetimeCost.result.weekly,
      calculations.inputs.netCost.weekly
    ),
    getTableRow(
      "Car Loan",
      null,
      calculations.inputs.carLoanInterestRate,
      calculations.carloan.interest.result,
      calculations.carloan.lifetimeCost.result.total,
      calculations.carloan.lifetimeCost.result.weekly,
      calculations.inputs.netCost.weekly
    ),
  ];

  const tableProps: APTableProps<TableDataDefinition> = {
    columns: [
      "method",
      "upfrontCost",
      "rate",
      "totalInterest",
      "lifetimeCost",
      "weeklyLifetimeCost",
      "costComparedToNovated",
    ],
    headerRows: getTableHeaderRows(),
    rows: rows,
  };

  return <APTable {...tableProps} />;
};

export default FinanceComparisonTable;
